import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { supabase } from '../../lib/supabase';
import { 
  FileText,
  Folder,
  Star,
  Calendar,
  Clock,
  CheckCircle,
  Edit3,
  Search,
  Target,
  TrendingUp,
  AlertCircle,
  ExternalLink
} from 'lucide-react';

interface ArticleStats {
  total: number;
  published: number;
  drafts: number;
  scheduled: number;
}

interface ProjectStats {
  total: number;
  inProgress: number;
  completed: number;
  featured: number;
}

interface RecentActivity {
  id: string;
  title: string;
  action: string;
  timestamp: string;
  type: 'article' | 'project';
}

interface SEOStats {
  articlesWithSEO: number;
  projectsWithSEO: number;
  totalSEOOptimized: number;
  averageSEOScore: number;
}

export function AdminDashboardRevamped() {
  const [articleStats, setArticleStats] = useState<ArticleStats>({
    total: 0,
    published: 0,
    drafts: 0,
    scheduled: 0
  });
  const [projectStats, setProjectStats] = useState<ProjectStats>({
    total: 0,
    inProgress: 0,
    completed: 0,
    featured: 0
  });
  const [seoStats, setSeoStats] = useState<SEOStats>({
    articlesWithSEO: 0,
    projectsWithSEO: 0,
    totalSEOOptimized: 0,
    averageSEOScore: 0
  });
  const [recentActivity, setRecentActivity] = useState<RecentActivity[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        // Fetch projects first
        const { data: projectsData, error: projectsError } = await supabase
          .from('projects')
          .select('*')
          .order('created_at', { ascending: false });

        if (projectsError) throw projectsError;
        
        const projectsList = (projectsData || []).map((item: any) => ({
          ...item,
          slug: item.slug || undefined,
          description: item.description || '',
          tech_stack: item.tech_stack || [],
          features: item.key_features || [],
          status: item.status || 'concept'
        }));
        const { data: articles, error } = await supabase
          .from('articles')
          .select('id, published, publish_date, meta_title, meta_description');

        if (error) throw error;

        const now = new Date();
        const totalArticles = articles.length;
        const publishedArticles = articles.filter(article => 
          article.published && (!article.publish_date || new Date(article.publish_date) <= now)
        ).length;
        const scheduledArticles = articles.filter(article => 
          article.published && article.publish_date && new Date(article.publish_date) > now
        ).length;
        const draftArticles = articles.filter(article => !article.published).length;

        // Calculate SEO stats for articles
        const articlesWithSEO = articles.filter(article => 
          article.meta_title && article.meta_description
        ).length;

        setArticleStats({
          total: totalArticles,
          published: publishedArticles,
          drafts: draftArticles,
          scheduled: scheduledArticles
        });

        // Calculate project stats
        const launchedProjects = projectsList.filter(p => p.status === 'launched').length;
        const inProgressProjects = projectsList.filter(p => 
          p.status === 'in_development' || p.status === 'prototype' || p.status === 'mvp' || p.status === 'concept'
        ).length;
        const featuredProjects = projectsList.filter(p => p.is_featured).length;

        // Calculate SEO stats for projects
        const projectsWithSEO = projectsList.filter(p => 
          p.meta_title && p.meta_description
        ).length;

        setProjectStats({
          total: projectsList.length,
          inProgress: inProgressProjects,
          completed: launchedProjects,
          featured: featuredProjects
        });

        setSeoStats({
          articlesWithSEO,
          projectsWithSEO,
          totalSEOOptimized: articlesWithSEO + projectsWithSEO,
          averageSEOScore: Math.round(((articlesWithSEO + projectsWithSEO) / (totalArticles + projectsList.length)) * 100)
        });

        // Fetch recent activity
        const { data: activity, error: activityError } = await supabase
          .from('articles')
          .select('id, title, updated_at, published')
          .order('updated_at', { ascending: false })
          .limit(5);

        if (activityError) throw activityError;

        const recentArticles = activity.map(item => ({
          id: item.id,
          title: item.title,
          action: item.published ? 'Published' : 'Updated Draft',
          timestamp: new Date(item.updated_at || '').toLocaleString(),
          type: 'article' as const
        }));

        // Add recent projects
        const recentProjects = projectsList
          .sort((a, b) => new Date(b.updated_at || b.created_at || 0).getTime() - new Date(a.updated_at || a.created_at || 0).getTime())
          .slice(0, 3)
          .map(project => ({
            id: project.id,
            title: project.title,
            action: 'Updated',
            timestamp: new Date(project.updated_at || project.created_at).toLocaleString(),
            type: 'project' as const
          }));

        setRecentActivity([...recentArticles, ...recentProjects]
          .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
          .slice(0, 6)
        );
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchStats();
  }, []);

  const quickActions = [
    {
      title: 'New Article',
      description: 'Create a new blog post',
      icon: FileText,
      href: '/admin/articles/new',
      color: 'from-blue-500 to-cyan-500'
    },
    {
      title: 'New Project',
      description: 'Add a new project',
      icon: Folder,
      href: '/admin/projects/new',
      color: 'from-purple-500 to-pink-500'
    },
    {
      title: 'SEO Review',
      description: 'Optimize content for search',
      icon: Search,
      href: '/admin/articles',
      color: 'from-green-500 to-emerald-500'
    },
    {
      title: 'Site Analytics',
      description: 'View on Netlify',
      icon: TrendingUp,
      href: 'https://app.netlify.com',
      color: 'from-orange-500 to-red-500',
      external: true
    }
  ];

  const contentStats = [
    {
      title: 'Total Articles',
      value: articleStats.total,
      icon: FileText,
      color: 'text-blue-600 dark:text-blue-400',
      bgColor: 'bg-blue-100 dark:bg-blue-900/30'
    },
    {
      title: 'Published',
      value: articleStats.published,
      icon: CheckCircle,
      color: 'text-green-600 dark:text-green-400',
      bgColor: 'bg-green-100 dark:bg-green-900/30'
    },
    {
      title: 'Drafts',
      value: articleStats.drafts,
      icon: Edit3,
      color: 'text-yellow-600 dark:text-yellow-400',
      bgColor: 'bg-yellow-100 dark:bg-yellow-900/30'
    },
    {
      title: 'Scheduled',
      value: articleStats.scheduled,
      icon: Clock,
      color: 'text-purple-600 dark:text-purple-400',
      bgColor: 'bg-purple-100 dark:bg-purple-900/30'
    },
    {
      title: 'Total Projects',
      value: projectStats.total,
      icon: Folder,
      color: 'text-indigo-600 dark:text-indigo-400',
      bgColor: 'bg-indigo-100 dark:bg-indigo-900/30'
    },
    {
      title: 'In Progress',
      value: projectStats.inProgress,
      icon: Clock,
      color: 'text-orange-600 dark:text-orange-400',
      bgColor: 'bg-orange-100 dark:bg-orange-900/30'
    },
    {
      title: 'Launched',
      value: projectStats.completed,
      icon: Target,
      color: 'text-emerald-600 dark:text-emerald-400',
      bgColor: 'bg-emerald-100 dark:bg-emerald-900/30'
    },
    {
      title: 'Featured',
      value: projectStats.featured,
      icon: Star,
      color: 'text-amber-600 dark:text-amber-400',
      bgColor: 'bg-amber-100 dark:bg-amber-900/30'
    }
  ];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gradient-to-r from-orange-500/10 via-pink-500/10 to-purple-500/10 dark:from-orange-500/20 dark:via-pink-500/20 dark:to-purple-500/20 rounded-3xl p-8 backdrop-blur-sm border border-orange-200/50 dark:border-orange-700/50"
      >
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
          <div>
            <h1 className="text-4xl font-bold bg-gradient-to-r from-orange-600 to-pink-600 dark:from-orange-400 dark:to-pink-400 bg-clip-text text-transparent mb-2">
              Dashboard
            </h1>
            <p className="text-gray-600 dark:text-gray-400 text-lg">
              Welcome back! Here's what's happening with your content.
            </p>
            <div className="flex items-center gap-4 mt-4 text-sm text-gray-500 dark:text-gray-400">
              <div className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                {new Date().toLocaleDateString('en-US', { 
                  weekday: 'long',
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })}
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Quick Actions */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
      >
        {quickActions.map((action) => (
          <Link
            key={action.title}
            to={action.href}
            target={action.external ? '_blank' : undefined}
            rel={action.external ? 'noopener noreferrer' : undefined}
            className="group relative bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl border border-gray-200/50 dark:border-gray-700/50 shadow-xl hover:shadow-2xl transition-all duration-300 overflow-hidden"
          >
            <div className={`absolute inset-0 bg-gradient-to-br ${action.color} opacity-0 group-hover:opacity-10 transition-opacity duration-300`} />
            <div className="relative p-6">
              <div className={`inline-flex p-3 rounded-xl bg-gradient-to-br ${action.color} text-white mb-4`}>
                <action.icon className="w-6 h-6" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2 flex items-center gap-2">
                {action.title}
                {action.external && <ExternalLink className="w-4 h-4" />}
              </h3>
              <p className="text-gray-600 dark:text-gray-400 text-sm">
                {action.description}
              </p>
            </div>
          </Link>
        ))}
      </motion.div>

      {/* Content Statistics */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4"
      >
        {contentStats.map((stat) => (
          <div
            key={stat.title}
            className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl border border-gray-200/50 dark:border-gray-700/50 shadow-xl p-4 text-center"
          >
            <div className={`inline-flex p-2 rounded-xl ${stat.bgColor} mb-3`}>
              <stat.icon className={`w-5 h-5 ${stat.color}`} />
            </div>
            <div className="text-2xl font-bold text-gray-900 dark:text-white mb-1">
              {stat.value}
            </div>
            <div className="text-xs text-gray-600 dark:text-gray-400">
              {stat.title}
            </div>
          </div>
        ))}
      </motion.div>

      {/* SEO Overview & Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* SEO Overview */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl border border-gray-200/50 dark:border-gray-700/50 shadow-xl p-6"
        >
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
              <Search className="w-5 h-5 text-green-600 dark:text-green-400" />
              SEO Overview
            </h2>
            <Link
              to="/admin/articles"
              className="text-sm text-green-600 dark:text-green-400 hover:text-green-700 dark:hover:text-green-300 font-medium"
            >
              Optimize →
            </Link>
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl">
              <div>
                <div className="text-sm text-gray-600 dark:text-gray-400">SEO Score</div>
                <div className="text-2xl font-bold text-gray-900 dark:text-white">
                  {seoStats.averageSEOScore}%
                </div>
              </div>
              <div className={`w-16 h-16 rounded-full flex items-center justify-center ${
                seoStats.averageSEOScore >= 80 ? 'bg-green-100 dark:bg-green-900/30' :
                seoStats.averageSEOScore >= 60 ? 'bg-yellow-100 dark:bg-yellow-900/30' :
                'bg-red-100 dark:bg-red-900/30'
              }`}>
                {seoStats.averageSEOScore >= 80 ? (
                  <CheckCircle className="w-8 h-8 text-green-600 dark:text-green-400" />
                ) : seoStats.averageSEOScore >= 60 ? (
                  <AlertCircle className="w-8 h-8 text-yellow-600 dark:text-yellow-400" />
                ) : (
                  <AlertCircle className="w-8 h-8 text-red-600 dark:text-red-400" />
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="text-center p-3 bg-gray-50 dark:bg-gray-700/50 rounded-xl">
                <div className="text-lg font-bold text-gray-900 dark:text-white">
                  {seoStats.articlesWithSEO}
                </div>
                <div className="text-xs text-gray-600 dark:text-gray-400">
                  Articles with SEO
                </div>
              </div>
              <div className="text-center p-3 bg-gray-50 dark:bg-gray-700/50 rounded-xl">
                <div className="text-lg font-bold text-gray-900 dark:text-white">
                  {seoStats.projectsWithSEO}
                </div>
                <div className="text-xs text-gray-600 dark:text-gray-400">
                  Projects with SEO
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Recent Activity */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl border border-gray-200/50 dark:border-gray-700/50 shadow-xl p-6"
        >
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
              <Clock className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              Recent Activity
            </h2>
          </div>

          <div className="space-y-3">
            {recentActivity.length > 0 ? (
              recentActivity.map((activity, index) => (
                <div
                  key={`${activity.type}-${activity.id}-${index}`}
                  className="flex items-center gap-3 p-3 hover:bg-gray-50 dark:hover:bg-gray-700/50 rounded-xl transition-colors"
                >
                  <div className={`p-2 rounded-lg ${
                    activity.type === 'article' 
                      ? 'bg-blue-100 dark:bg-blue-900/30' 
                      : 'bg-purple-100 dark:bg-purple-900/30'
                  }`}>
                    {activity.type === 'article' ? (
                      <FileText className={`w-4 h-4 ${
                        activity.type === 'article' 
                          ? 'text-blue-600 dark:text-blue-400' 
                          : 'text-purple-600 dark:text-purple-400'
                      }`} />
                    ) : (
                      <Folder className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-gray-900 dark:text-white truncate">
                      {activity.title}
                    </div>
                    <div className="text-xs text-gray-600 dark:text-gray-400">
                      {activity.action} • {new Date(activity.timestamp).toLocaleDateString()}
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                <Clock className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No recent activity</p>
              </div>
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
}
